import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Input,
  Label,
  FormGroup,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import axios from "axios";
import BASE_URL from "../../config";
import Select from "react-select";
import { 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin,
  hasPagePermissionWithAdmin
} from "../../components/Permission/PermissionService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { validateFile } from "../../utils/fileUploadHelper";

// API Helpers
import { getRooms, saveRoom } from "../../components/Api/GmsRoom";
import { getEquipments, saveEquipment } from "../../components/Api/GmsEquipment";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getTicketNotifications, markTicketNotificationAsRead, getServerTime } from "../../components/Api/GmsTicket";

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || authUser.user_Id || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
    employeeId: authUser.employeeId || user.user_EmployeeId || null,
    isAdmin: authUser.isAdmin || false
  };
};

const GmsMaintenanceTicketsPage = (props) => {
  const session = useMemo(() => getUserSession(), []);
  const [activeTickets, setActiveTickets] = useState([]);
  const [resolvedTickets, setResolvedTickets] = useState([]);
  const [activeTotal, setActiveTotal] = useState(0);
  const [resolvedTotal, setResolvedTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [resolvedPage, setResolvedPage] = useState(1);
  const [rooms, setRooms] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isEditUser = session.isAdmin || hasEditOrHigherPermission(permissions, '/GmsMaintenanceTickets');
  
  // Form State
  const [formData, setFormData] = useState({
    ticketType: 'M',
    businessId: null,
    branchId: null,
    roomId: null,
    equipmentId: null,
    priority: 2,
    description: '',
    comments: '',
    assignedTo: null,
    documentFile: null,
    cost: ''
  });

  // Update Modal State
  const [updateData, setUpdateData] = useState({
    statusId: 1,
    comment: '',
    assignedTo: null,
    cost: ''
  });

  // Ticket Log State
  const [ticketLogs, setTicketLogs] = useState([]);
  const [viewModal, setViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  // Technician Search in Form
  const [technicianSearchOptions, setTechnicianSearchOptions] = useState([]);
  const [technicianSearchLoading, setTechnicianSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const statusList = [
    { value: 1, label: 'Open', color: 'danger' },
    { value: 2, label: 'Assigned', color: 'info' },
    { value: 8, label: 'In Progress', color: 'primary' },
    { value: 3, label: 'OnHold', color: 'warning' },
    { value: 5, label: 'Reopened', color: 'primary' },
    { value: 7, label: 'Done', color: 'success' },
    { value: 6, label: 'Closed', color: 'success' },
    { value: 4, label: 'Cancelled', color: 'secondary' }
  ];

  const priorityOptions = [
    { value: 0, label: 'Critical', color: 'danger' },
    { value: 1, label: 'High', color: 'warning' },
    { value: 2, label: 'Medium', color: 'info' },
    { value: 3, label: 'Low', color: 'success' }
  ];

  // Quick Add Modal States
  const [roomModal, setRoomModal] = useState(false);
  const [roomFormData, setRoomFormData] = useState({ Room_Code: '', Room_Name_English: '', Room_Name_Arabic: '', Status: true });
  const [roomLoading, setRoomLoading] = useState(false);

  const [equipModal, setEquipModal] = useState(false);
  const [equipFormData, setEquipFormData] = useState({ Eq_Code: '', Eq_Name_English: '', Eq_Name_Arabic: '', PurchaseDate: '', ValueDate: '', Status: true });
  const [equipLoading, setEquipLoading] = useState(false);

  const [serverOffset, setServerOffset] = useState(0);

  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return "-";
    const start = new Date(startDate);
    
    let end;
    if (endDate) {
      end = new Date(endDate);
    } else {
      // Adust local current time by the offset between server and client
      end = new Date(Date.now() + serverOffset);
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));

    if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
    if (diffHours > 0) return `${diffHours}h ${diffMinutes}m`;
    return `${diffMinutes}m`;
  };

  useEffect(() => {
    loadSetupData();
  }, []);

  useEffect(() => {
    if (formData.businessId && formData.branchId) {
      loadTickets();
    }
  }, [formData.businessId, formData.branchId]);

  const loadSetupData = async () => {
    try {
      setLoading(true);
      const [busRes, roomsRes, equipRes, timeRes] = await Promise.all([
        getBusinesses({ business_Active: true }),
        getRooms({ Status: true }),
        getEquipments({ status: true }),
        getServerTime()
      ]);

      if (timeRes && timeRes.serverTime) {
        const serverTime = new Date(timeRes.serverTime);
        const localTime = new Date();
        const offset = serverTime - localTime;
        setServerOffset(offset);
      }

      setBusinesses(busRes.data || busRes || []);
      setRooms((roomsRes.data || roomsRes || []).map(r => ({ value: r.room_Id, label: r.room_Name_English })));
      setEquipments((equipRes.data || equipRes || []).map(e => ({ value: e.eq_Id || e.equipment_Id, label: e.eq_Name_English || e.equipment_Name_English })));

      // Auto-select based on session
      let initialBusId = session.companyId ? (typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId) : null;
      let initialBrId = session.branchId ? (typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId) : null;

      if (initialBusId) {
        await loadBranchesForBusiness(initialBusId);
      }
      
      setFormData(prev => ({ 
        ...prev, 
        businessId: initialBusId, 
        branchId: initialBrId 
      }));

    } catch (e) {
      console.error("Error loading setup data", e);
    } finally {
      setLoading(false);
    }
  };

  const loadBranchesForBusiness = async (busId) => {
    try {
      const res = await getBranches({ Business_Id: busId, branch_Active: true });
      setBranches(res.data || res || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadTickets = async (aPage = activePage, rPage = resolvedPage) => {
    if (!formData.businessId || !formData.branchId) return;
    try {
      setLoading(true);
      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      
      const basePayload = { 
        BusinessID: formData.businessId,
        BranchID: formData.branchId,
        PageSize: 5
      };

      if (!isEditUser) {
          basePayload.AssignedTo = session.employeeId;
      }

      // Load Active
      const resActive = await axios.post(`${BASE_URL}/api/Ticket/get`, { ...basePayload, IsActive: true, PageNumber: aPage }, authHeader);
      const actData = resActive.data || [];
      setActiveTickets(actData);
      setActiveTotal(actData.length > 0 ? (actData[0].totalRecords || actData[0].TotalRecords || 0) : 0);

      // Load Resolved
      const resResolved = await axios.post(`${BASE_URL}/api/Ticket/get`, { ...basePayload, IsActive: false, PageNumber: rPage }, authHeader);
      const resData = resResolved.data || [];
      setResolvedTickets(resData);
      setResolvedTotal(resData.length > 0 ? (resData[0].totalRecords || resData[0].TotalRecords || 0) : 0);
      
    } catch (error) {
      console.error("Error loading tickets", error);
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.businessId && formData.branchId) {
      loadTickets(activePage, resolvedPage);
    }
  }, [activePage, resolvedPage, formData.businessId, formData.branchId]);

  const searchTechniciansManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) return;
    setTechnicianSearchLoading(true);
    try {
      const res = await searchEmployees(inputValue, 1, 50, formData.businessId, formData.branchId);
      const options = (res.items || []).map(e => ({
        value: e.employee_Id,
        label: `${e.employee_NameEn || 'Unknown'} (${e.emp_No || 'N/A'})`
      }));
      setTechnicianSearchOptions(options);
    } catch (error) {
      console.error("Error searching technicians", error);
    } finally {
      setTechnicianSearchLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!formData.businessId || !formData.branchId || !formData.description) {
      return toast.warning("Please fill all required fields");
    }

    try {
      setLoading(true);
      const authHeader = { headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        'Content-Type': 'multipart/form-data'
      } };

      const ticketFormData = new FormData();
      ticketFormData.append('ticketType', formData.ticketType);
      ticketFormData.append('businessId', formData.businessId);
      ticketFormData.append('branchId', formData.branchId);
      ticketFormData.append('roomId', formData.roomId?.value || '');
      ticketFormData.append('equipmentId', formData.equipmentId?.value || '');
      ticketFormData.append('priority', formData.priority);
      ticketFormData.append('description', formData.description);
      ticketFormData.append('comments', formData.comments || '');
      ticketFormData.append('createdBy', session.userId);
      ticketFormData.append('assignedTo', formData.assignedTo?.value || '');
      ticketFormData.append('cost', formData.cost || '');
      
      if (formData.documentFile) {
        ticketFormData.append('documentFile', formData.documentFile);
      }

      await axios.post(`${BASE_URL}/api/Ticket/save`, ticketFormData, authHeader);
      Swal.fire("Success", "Ticket logged successfully!", "success");
      
      // Reset form (keeping company/branch)
      setFormData({
        ...formData,
        roomId: null,
        equipmentId: null,
        priority: 2,
        description: '',
        comments: '',
        assignedTo: null,
        documentFile: null,
        cost: ''
      });
      document.getElementById('ticketDocument').value = '';
      
      loadTickets();
    } catch (error) {
      console.error("Error creating ticket", error);
      toast.error("Failed to log ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTicket = async () => {
    try {
      setLoading(true);
      const authHeader = { headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        'Content-Type': 'multipart/form-data'
      } };

      const updateFormData = new FormData();
      updateFormData.append('ticketID', selectedTicket.ticketID);
      updateFormData.append('statusID', updateData.statusId);
      updateFormData.append('comment', updateData.comment);
      // Only Admin/Edit user can change technician
      // If updateData.assignedTo is strictly null, the user cleared it, so use '-1' to signal clearance
      const finalAssignedTo = isEditUser 
        ? (updateData.assignedTo ? updateData.assignedTo.value : '-1') 
        : (selectedTicket.assignedTo || '');

      if (finalAssignedTo !== '-1' && !finalAssignedTo && updateData.statusId !== 1 && updateData.statusId !== 4) {
         toast.warning("A ticket must have an assigned technician for statuses other than Open or Cancelled.");
         setLoading(false);
         return;
      }
      
      updateFormData.append('assignedTo', finalAssignedTo);
      updateFormData.append('businessId', formData.businessId);
      updateFormData.append('branchId', formData.branchId || '');
      updateFormData.append('createdBy', session.userId);
      updateFormData.append('priority', selectedTicket.priority);
      updateFormData.append('description', selectedTicket.description);
      updateFormData.append('ticketType', selectedTicket.ticketType);
      updateFormData.append('cost', updateData.cost || '');

      await axios.post(`${BASE_URL}/api/Ticket/save`, updateFormData, authHeader);
      Swal.fire("Success", "Ticket updated successfully!", "success");
      setModal(false);
      loadTickets();
    } catch (error) {
      console.error("Error updating ticket", error);
      toast.error("Failed to update ticket");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (p) => {
    const opt = priorityOptions.find(o => o.value === p);
    return <Badge color={`soft-${opt?.color || "secondary"}`} className={`text-${opt?.color || "secondary"} px-2 py-1`}>{opt?.label || "Unknown"}</Badge>;
  };

  const getStatusBadge = (s) => {
    const opt = statusList.find(o => o.value === s);
    return <Badge color={`soft-${opt?.color || "secondary"}`} pill className={`text-${opt?.color || "secondary"} px-2 py-1`}>{opt?.label || "Unknown"}</Badge>;
  };

  const toggleModal = (ticket) => {
    if (ticket) {
      setSelectedTicket(ticket);
      setUpdateData({
        statusId: ticket.statusId,
        comment: '',
        assignedTo: ticket.assignedTo ? { value: ticket.assignedTo, label: ticket.assignedToName } : null,
        cost: ticket.cost || ''
      });
    }
    setModal(!modal);
  };

  const toggleViewModal = async (ticket) => {
    if (ticket) {
      setSelectedTicket(ticket);
      setViewLoading(true);
      setViewModal(true);
      setTicketLogs([]); // Reset logs
      try {
        const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
        const logsRes = await axios.get(`${BASE_URL}/api/Ticket/logs/${ticket.ticketID}`, authHeader);
        const logs = logsRes.data || [];
        setTicketLogs(logs);

        // Mark unread logs as read for the current user using the stable session
        const userId = session.userId;
        const employeeId = session.employeeId;
        
        const unreadLogs = logs.filter(l => 
          l.isRead === false && 
          (l.notificationRecipientID === userId || l.notificationRecipientID === employeeId)
        );

        for (const log of unreadLogs) {
          try {
            await markTicketNotificationAsRead(log.logID, log.notificationRecipientID);
          } catch (e) {
             console.error("Error marking as read", e);
          }
        }
      } catch (error) {
        console.error("Error loading logs", error);
        toast.error("Failed to load ticket history");
      } finally {
        setViewLoading(false);
      }
    } else {
      setViewModal(false);
    }
  };

  const handleViewDocument = async (ticket) => {
    try {
      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const docRes = await axios.get(`${BASE_URL}/api/Ticket/documents/${ticket.ticketID}`, authHeader);
      const docs = docRes.data || [];
      if (docs.length > 0) {
        window.open(`${BASE_URL}${docs[0].documentPath}`, '_blank');
      } else {
        toast.info("No document found for this ticket.");
      }
    } catch (error) {
      console.error("Error loading documents", error);
      toast.error("Failed to load document path");
    }
  };

  const handleQuickAddRoom = async (e) => {
    e.preventDefault();
    if (!roomFormData.Room_Name_English) return toast.warning("Room Name (English) is required");
    setRoomLoading(true);
    try {
      const res = await saveRoom(roomFormData);
      if (res) {
        Swal.fire("Success", "Room saved successfully!", "success");
        setRoomModal(false);
        setRoomFormData({ Room_Code: '', Room_Name_English: '', Room_Name_Arabic: '', Status: true });
        // Reload rooms and select the new one
        const roomsRes = await getRooms({ Status: true });
        const updatedRooms = (roomsRes.data || roomsRes || []).map(r => ({ value: r.room_Id, label: r.room_Name_English }));
        setRooms(updatedRooms);
        
        // Find the newly created room (usually the one with the highest ID or matching name)
        const newRoom = updatedRooms.find(r => r.label === roomFormData.Room_Name_English);
        if (newRoom) setFormData(prev => ({ ...prev, roomId: newRoom }));
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to add room");
    } finally {
      setRoomLoading(false);
    }
  };

  const handleQuickAddEquipment = async (e) => {
    e.preventDefault();
    if (!equipFormData.Eq_Name_English) return toast.warning("Equipment Name (English) is required");
    if (!equipFormData.PurchaseDate) return Swal.fire("Required", "Please select Purchase Date", "warning");
    if (!equipFormData.ValueDate) return Swal.fire("Required", "Please select Value Date", "warning");
    
    setEquipLoading(true);
    try {
      const res = await saveEquipment(equipFormData);
      if (res) {
        Swal.fire("Success", "Equipment saved successfully!", "success");
        setEquipModal(false);
        setEquipFormData({ Eq_Code: '', Eq_Name_English: '', Eq_Name_Arabic: '', PurchaseDate: '', ValueDate: '', Status: true });
        // Reload equipments and select the new one
        const equipRes = await getEquipments({ status: true });
        const updatedEquips = (equipRes.data || equipRes || []).map(e => ({ value: e.eq_Id || e.equipment_Id, label: e.eq_Name_English || e.equipment_Name_English }));
        setEquipments(updatedEquips);
        
        const newEquip = updatedEquips.find(e => e.label === equipFormData.Eq_Name_English);
        if (newEquip) setFormData(prev => ({ ...prev, equipmentId: newEquip }));
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to add equipment");
    } finally {
      setEquipLoading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file (Images and PDFs only, max 5MB, no video)
      const validation = validateFile(file, { 
        maxSizeMB: 5, 
        allowedTypes: ['image/jpeg', 'image/png', 'application/pdf'] 
      });

      if (!validation.valid) {
        // Reset file input if validation fails
        e.target.value = "";
        return;
      }

      setFormData({ ...formData, documentFile: file });
    }
  };

  const businessOptions = businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn || b.business_Name }));
  const branchOptions = branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn || b.branch_Name }));
  const isBusinessDisabled = !!session.companyId;
  const isBranchDisabled = !!session.branchId;

  // Metrics
  const metrics = {
    open: activeTickets.filter(t => t.statusId === 1).length,
    assigned: activeTickets.filter(t => t.statusId === 2).length,
    inProgress: activeTickets.filter(t => t.statusId === 8).length,
    closedTotal: resolvedTotal
  };

  const isSelected = formData.businessId && formData.branchId;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="GMS" breadcrumbItem="Maintenance" />
          
          {/* Top Filter Selection (Selection First Policy) */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm border-0 mb-4">
                <CardBody className="py-3">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <FormGroup className="mb-0 d-flex align-items-center">
                        <Label className="me-3 mb-0 flex-shrink-0">Company <span className="text-danger">*</span></Label>
                        <Select
                          value={businessOptions.find(o => o.value === formData.businessId)}
                          onChange={(opt) => {
                            setFormData({ ...formData, businessId: opt?.value, branchId: null });
                            loadBranchesForBusiness(opt?.value);
                          }}
                          options={businessOptions}
                          isDisabled={isBusinessDisabled}
                          styles={{...selectStyles, container: base => ({...base, flexGrow: 1})}}
                          placeholder="Select Company..."
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-0 d-flex align-items-center">
                        <Label className="me-3 mb-0 flex-shrink-0">Branch <span className="text-danger">*</span></Label>
                        <Select
                          value={branchOptions.find(o => o.value === formData.branchId)}
                          onChange={(opt) => setFormData({ ...formData, branchId: opt?.value })}
                          options={branchOptions}
                          isDisabled={isBranchDisabled || !formData.businessId}
                          styles={{...selectStyles, container: base => ({...base, flexGrow: 1})}}
                          placeholder="Select Branch..."
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {isSelected ? (
            <>


              <Row>
                {/* Left Column: Log Issue Form - Visible only to Edit users */}
                {isEditUser && (
                  <Col lg={4}>
                    <Card className="shadow-sm border border-info border-2 h-100">
                      <CardBody>
                        <CardTitle className="mb-4 text-info fw-bold d-flex align-items-center">
                          <i className="bx bx-plus-circle me-2 fs-3"></i> Log New Ticket
                        </CardTitle>
                        <Form>
                          <Row>
                            <Col md={12}>
                              <FormGroup>
                                <Label className="fw-bold">Ticket Type</Label>
                                <div className="d-flex gap-4 mb-2">
                                   <div className="form-check">
                                      <Input 
                                        type="radio" 
                                        id="typeM" 
                                        className="form-check-input" 
                                        checked={formData.ticketType === 'M'} 
                                        onChange={() => setFormData({ ...formData, ticketType: 'M' })} 
                                      />
                                      <Label className="form-check-label" htmlFor="typeM">Maintenance</Label>
                                   </div>
                                   <div className="form-check">
                                      <Input 
                                        type="radio" 
                                        id="typeT" 
                                        className="form-check-input" 
                                        checked={formData.ticketType === 'T'} 
                                        onChange={() => setFormData({ ...formData, ticketType: 'T', roomId: null, equipmentId: null })} 
                                      />
                                      <Label className="form-check-label" htmlFor="typeT">Normal Ticket</Label>
                                   </div>
                                </div>
                              </FormGroup>
                            </Col>

                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold d-flex align-items-center">
                                  Room
                                  <button 
                                    type="button" 
                                    className="btn btn-sm btn-soft-info p-0 ms-2 rounded-circle d-flex align-items-center justify-content-center" 
                                    style={{ width: '20px', height: '20px' }}
                                    onClick={() => setRoomModal(true)}
                                    title="Quick Add Room"
                                  >
                                    <i className="bx bx-plus"></i>
                                  </button>
                                </Label>
                                <Select
                                  value={formData.roomId}
                                  onChange={(opt) => setFormData({ ...formData, roomId: opt })}
                                  options={rooms}
                                  isClearable
                                  isDisabled={formData.ticketType === 'T'}
                                  styles={selectStyles}
                                  placeholder="Select"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold d-flex align-items-center">
                                  Equipment
                                  <button 
                                    type="button" 
                                    className="btn btn-sm btn-soft-info p-0 ms-2 rounded-circle d-flex align-items-center justify-content-center" 
                                    style={{ width: '20px', height: '20px' }}
                                    onClick={() => setEquipModal(true)}
                                    title="Quick Add Equipment"
                                  >
                                    <i className="bx bx-plus"></i>
                                  </button>
                                </Label>
                                <Select
                                  value={formData.equipmentId}
                                  onChange={(opt) => setFormData({ ...formData, equipmentId: opt })}
                                  options={equipments}
                                  isClearable
                                  isDisabled={formData.ticketType === 'T'}
                                  styles={selectStyles}
                                  placeholder="Select"
                                />
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Priority</Label>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                  {priorityOptions.map((opt) => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      className={`btn btn-sm ${formData.priority === opt.value ? `btn-${opt.color}` : `btn-soft-${opt.color}`} border-0 px-3 py-1 fw-bold transition-all`}
                                      onClick={() => setFormData({ ...formData, priority: opt.value })}
                                      style={{ 
                                        minWidth: '80px',
                                        boxShadow: formData.priority === opt.value ? '0 4px 8px rgba(0,0,0,0.15)' : 'none',
                                        transform: formData.priority === opt.value ? 'scale(1.05)' : 'scale(1)',
                                      }}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Assignee</Label>
                                <Select
                                   value={formData.assignedTo}
                                   onChange={(opt) => setFormData({ ...formData, assignedTo: opt })}
                                   options={technicianSearchOptions}
                                   onInputChange={(inputValue, { action }) => {
                                      if (action === 'input-change') {
                                         if (searchTimeout) clearTimeout(searchTimeout);
                                         const timeout = setTimeout(() => {
                                            searchTechniciansManually(inputValue);
                                         }, 400);
                                         setSearchTimeout(timeout);
                                      }
                                   }}
                                   isLoading={technicianSearchLoading}
                                   noOptionsMessage={({ inputValue }) => {
                                      if (technicianSearchLoading) return "Searching...";
                                      if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                                      return "No technicians found";
                                   }}
                                   isClearable
                                   styles={selectStyles}
                                   placeholder="Search Tech..."
                                />
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Issue Description <span className="text-danger">*</span></Label>
                                <Input
                                  type="textarea"
                                  rows="2"
                                  placeholder="Describe the problem..."
                                  value={formData.description}
                                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Comments / Remarks</Label>
                                <Input
                                  type="textarea"
                                  rows="2"
                                  placeholder="Notes..."
                                  value={formData.comments}
                                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                                />
                              </FormGroup>
                            </Col>



                            <Col md={12}>
                               <FormGroup className="mb-4">
                                 <Label className="fw-bold">Cost</Label>
                                 <Input
                                   type="number"
                                   placeholder="0.00"
                                   value={formData.cost}
                                   onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                                 />
                               </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Upload Attachment</Label>
                                <Input
                                  type="file"
                                  id="ticketDocument"
                                  className="form-control"
                                  onChange={handleFileChange}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <div className="d-flex gap-2 mt-4">
                            <Button 
                              color="info" 
                              className="flex-grow-1 py-2 fw-bold d-flex align-items-center justify-content-center" 
                              onClick={handleCreateTicket} 
                              disabled={loading}
                              style={{ borderRadius: '8px' }}
                            >
                              {loading ? <i className="bx bx-loader bx-spin me-2"></i> : <i className="bx bx-save me-2"></i>}
                              Save Ticket
                            </Button>
                            <Button 
                              color="light" 
                              className="flex-grow-1 py-2 fw-bold" 
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  roomId: null,
                                  equipmentId: null,
                                  priority: 2,
                                  description: '',
                                  comments: '',
                                  assignedTo: null,
                                  documentFile: null,
                                  cost: ''
                                });
                                if(document.getElementById('ticketDocument')) document.getElementById('ticketDocument').value = '';
                                toast.info("Form cleared");
                              }} 
                              disabled={loading}
                              style={{ borderRadius: '8px' }}
                            >
                              Clear
                            </Button>
                          </div>
                        </Form>
                      </CardBody>
                    </Card>
                  </Col>
                )}

                {/* Right Column: Tickets Table */}
                <Col lg={isEditUser ? 8 : 12}>
                  {/* Dashboard Metrics - Compact Version */}
                  <Row className="mb-0">
                    {[
                      { label: 'Open Issues', value: metrics.open, color: 'danger', icon: 'bx-error' },
                      { label: 'Assigned', value: metrics.assigned, color: 'info', icon: 'bx-user-plus' },
                      { label: 'In Progress', value: metrics.inProgress, color: 'warning', icon: 'bx-loader' },
                      { label: 'Closed', value: metrics.closedTotal, color: 'success', icon: 'bx-check-circle' }
                    ].map((m, idx) => (
                      <Col xl={3} sm={6} key={idx} className="mb-0">
                        <Card className="shadow-sm transition-all" style={{ borderRadius: '12px', border: `1px solid var(--bs-${m.color})`, borderLeftWidth: '4px' }}>
                          <CardBody className="p-2 px-3">
                            <div className="d-flex align-items-center">
                              <div className={`avatar-xs bg-soft-${m.color} text-${m.color} rounded-circle d-flex align-items-center justify-content-center me-3`} style={{ width: '38px', height: '38px', fontSize: '18px' }}>
                                <i className={`bx ${m.icon}`}></i>
                              </div>
                              <div className="text-start">
                                <p className="text-muted fw-bold text-uppercase mb-0" style={{ fontSize: '11px', letterSpacing: '0.4px' }}>{m.label}</p>
                                <h4 className={`mb-0 text-${m.color} fw-bold`}>{m.value}</h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  <Card className="shadow-sm border border-warning border-2 mb-2">
                    <CardBody className="pb-3">
                      <CardTitle className="mb-3 d-flex justify-content-between align-items-center">
                         <span className="text-warning fw-bold d-flex align-items-center fs-5">
                            <i className="bx bx-list-ul me-2 fs-4"></i> {isEditUser ? 'Active Tickets' : 'My Assigned Tickets'}
                         </span>
                         
                         <div className="d-flex align-items-center gap-2">
                           {activeTotal > 0 && (
                              <div className="d-flex align-items-center bg-light rounded px-1 border">
                                 <button 
                                   className={`btn btn-sm btn-link p-0 text-decoration-none ${activePage === 1 ? 'text-muted' : 'text-primary'}`} 
                                   style={{ width: '24px', height: '24px' }}
                                   onClick={() => setActivePage(activePage - 1)} 
                                   disabled={activePage === 1}
                                 >
                                   <i className="bx bx-chevron-left" style={{ fontSize: '1.2rem' }}></i>
                                 </button>
                                 <span className="text-dark mx-1" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                    {activePage} <span className="text-muted">/ {Math.ceil(activeTotal / 5)}</span>
                                 </span>
                                 <button 
                                   className={`btn btn-sm btn-link p-0 text-decoration-none ${activePage === Math.ceil(activeTotal / 5) ? 'text-muted' : 'text-primary'}`} 
                                   style={{ width: '24px', height: '24px' }}
                                   onClick={() => setActivePage(activePage + 1)} 
                                   disabled={activePage === Math.ceil(activeTotal / 5)}
                                 >
                                   <i className="bx bx-chevron-right" style={{ fontSize: '1.2rem' }}></i>
                                 </button>
                              </div>
                           )}
                           <Button size="sm" color="light" className="rounded p-1 d-flex align-items-center justify-content-center" style={{width: '26px', height: '26px'}} onClick={() => loadTickets()} disabled={loading} title="Refresh">
                             <i className={`bx bx-refresh fs-5 ${loading ? 'bx-spin' : ''}`}></i>
                           </Button>
                         </div>
                      </CardTitle>
                      <div style={{ maxHeight: '350px', overflowY: 'auto', borderBottom: '1px solid #f1f3f5' }}>
                        <Table className="table-nowrap table-hover mb-0 align-middle w-100">
                          <thead className="table-light text-primary sticky-top" style={{ zIndex: 5, top: 0 }}>
                            <tr style={{ position: 'sticky', top: 0, zIndex: 6 }}>
                              <th style={{ width: '10%' }}>Code</th>
                              <th>Room/Equip</th>
                              <th style={{ width: '12%' }}>Priority</th>
                              <th style={{ width: '12%' }}>Status</th>
                              <th style={{ width: '15%' }}>Time Passed</th>
                              {isEditUser && <th style={{ width: '15%' }}>Assignee</th>}
                              <th style={{ width: '10%' }} className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeTickets.map((ticket) => (
                              <tr key={ticket.ticketID}>
                                <td className="fw-bold text-primary">#{ticket.ticketCode}</td>
                                <td>
                                   <div className="fw-medium text-dark">
                                      {ticket.roomName && ticket.equipmentName ? `${ticket.roomName} - ${ticket.equipmentName}` : (ticket.roomName || ticket.equipmentName || "-")}
                                   </div>
                                </td>
                                <td>{getPriorityBadge(ticket.priority)}</td>
                                <td>{getStatusBadge(ticket.statusId)}</td>
                                <td className="fw-bold text-muted">
                                   <div style={{fontSize: '0.75rem'}}>
                                      <i className="bx bx-time-five me-1"></i>
                                      {calculateDuration(ticket.createdDate)}
                                   </div>
                                </td>
                                {isEditUser && <td className="fw-medium text-primary">{ticket.assignedToName || <span className="text-danger small italic text-uppercase" style={{fontSize: '0.7rem'}}>Unassigned</span>}</td>}
                                <td className="text-center">
                                   <div className="d-flex gap-2 justify-content-center">
                                      <Button
                                        size="sm"
                                        color="soft-info"
                                        id={`view-${ticket.ticketID}`}
                                        className="btn-rounded"
                                        onClick={() => toggleViewModal(ticket)}
                                      >
                                        <i className="bx bx-show-alt"></i>
                                      </Button>
                                      {ticket.documentCount > 0 && (
                                        <Button
                                          size="sm"
                                          color="soft-dark"
                                          className="btn-rounded"
                                          onClick={() => handleViewDocument(ticket)}
                                        >
                                          <i className="bx bx-file"></i>
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        color="soft-primary"
                                        className="btn-rounded"
                                        onClick={() => toggleModal(ticket)}
                                      >
                                        <i className="bx bx-edit"></i>
                                      </Button>
                                   </div>
                                </td>
                              </tr>
                            ))}
                            {activeTickets.length === 0 && (
                              <tr>
                                <td colSpan={isEditUser ? "7" : "6"} className="text-center py-5 text-muted italic small">No active tickets found.</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Completed History */}
                  <Card className="shadow-sm border border-success border-2">
                    <CardBody className="pb-3">
                      <CardTitle className="mb-3 d-flex justify-content-between align-items-center">
                         <span className="text-success fw-bold d-flex align-items-center fs-5">
                            <i className="bx bx-history me-2 fs-4"></i> Resolved Issues History
                         </span>
                         <div className="d-flex align-items-center gap-2">
                           {resolvedTotal > 0 && (
                              <div className="d-flex align-items-center bg-light rounded px-1 border">
                                 <button 
                                   className={`btn btn-sm btn-link p-0 text-decoration-none ${resolvedPage === 1 ? 'text-muted' : 'text-primary'}`} 
                                   style={{ width: '24px', height: '24px' }}
                                   onClick={() => setResolvedPage(resolvedPage - 1)} 
                                   disabled={resolvedPage === 1}
                                 >
                                   <i className="bx bx-chevron-left" style={{ fontSize: '1.2rem' }}></i>
                                 </button>
                                 <span className="text-dark mx-1" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                    {resolvedPage} <span className="text-muted">/ {Math.ceil(resolvedTotal / 5)}</span>
                                 </span>
                                 <button 
                                   className={`btn btn-sm btn-link p-0 text-decoration-none ${resolvedPage === Math.ceil(resolvedTotal / 5) ? 'text-muted' : 'text-primary'}`} 
                                   style={{ width: '24px', height: '24px' }}
                                   onClick={() => setResolvedPage(resolvedPage + 1)} 
                                   disabled={resolvedPage === Math.ceil(resolvedTotal / 5)}
                                 >
                                   <i className="bx bx-chevron-right" style={{ fontSize: '1.2rem' }}></i>
                                 </button>
                              </div>
                           )}
                           <Button size="sm" color="light" className="rounded p-1 d-flex align-items-center justify-content-center" style={{width: '26px', height: '26px'}} onClick={() => loadTickets()} disabled={loading} title="Refresh">
                             <i className={`bx bx-refresh fs-5 ${loading ? 'bx-spin' : ''}`}></i>
                           </Button>
                         </div>
                      </CardTitle>
                      <div style={{ maxHeight: '350px', overflowY: 'auto', borderBottom: '1px solid #f1f3f5' }}>
                        <Table className="table-nowrap table-hover mb-0 align-middle w-100">
                          <thead className="table-light text-success sticky-top" style={{ zIndex: 5, top: 0 }}>
                            <tr style={{ position: 'sticky', top: 0, zIndex: 6 }}>
                              <th style={{ width: '10%' }}>Code</th>
                              <th>Room/Equip</th>
                              <th style={{ width: '12%' }}>Priority</th>
                              <th style={{ width: '12%' }}>Cost</th>
                              <th style={{ width: '15%' }}>Time Taken</th>
                              {isEditUser && <th style={{ width: '15%' }}>Assignee</th>}
                              <th style={{ width: '10%' }} className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resolvedTickets.map((ticket) => (
                              <tr key={ticket.ticketID}>
                                <td className="fw-bold text-primary">#{ticket.ticketCode}</td>
                                <td>
                                   <div className="fw-medium text-dark">
                                      {ticket.roomName && ticket.equipmentName ? `${ticket.roomName} - ${ticket.equipmentName}` : (ticket.roomName || ticket.equipmentName || "-")}
                                   </div>
                                </td>
                                <td>{getPriorityBadge(ticket.priority)}</td>
                                <td className="fw-bold text-success">
                                   {ticket.cost || "0.00"}
                                </td>
                                <td>
                                   <div className="fw-bold text-success" style={{fontSize: '0.75rem'}}>
                                      <i className="bx bx-timer me-1"></i>
                                      {calculateDuration(ticket.createdDate, ticket.resolvedDate)}
                                   </div>
                                </td>
                                {isEditUser && <td className="fw-medium text-primary">{ticket.assignedToName || <span className="text-danger small italic text-uppercase" style={{fontSize: '0.7rem'}}>N/A</span>}</td>}
                                <td className="text-center">
                                   <div className="d-flex gap-2 justify-content-center">
                                      <Button
                                        size="sm"
                                        color="soft-info"
                                        className="btn-rounded"
                                        onClick={() => toggleViewModal(ticket)}
                                      >
                                        <i className="bx bx-show-alt"></i>
                                      </Button>
                                      {ticket.documentCount > 0 && (
                                        <Button
                                          size="sm"
                                          color="soft-dark"
                                          className="btn-rounded"
                                          onClick={() => handleViewDocument(ticket)}
                                        >
                                          <i className="bx bx-file"></i>
                                        </Button>
                                      )}
                                      {(session.isAdmin || session.userId === ticket.createdBy) && (
                                        <Button
                                          size="sm"
                                          color="soft-primary"
                                          className="btn-rounded"
                                          onClick={() => toggleModal(ticket)}
                                        >
                                          <i className="bx bx-edit"></i>
                                        </Button>
                                      )}
                                   </div>
                                </td>
                              </tr>
                            ))}
                            {resolvedTickets.length === 0 && (
                              <tr>
                                <td colSpan={isEditUser ? "7" : "6"} className="text-center py-5 text-muted italic small">No history records found.</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Row>
               <Col lg={12} className="text-center mt-5">
                  <div className="p-5">
                     <i className="bx bx-search-alt text-muted display-1 mb-4"></i>
                     <h3 className="text-muted">Select Company and Branch</h3>
                  </div>
               </Col>
            </Row>
          )}

          {/* Update Modal */}
          <Modal isOpen={modal} toggle={() => setModal(!modal)} centered>
            <ModalHeader toggle={() => setModal(!modal)} className="bg-primary text-white" style={{ color: '#fff' }}>
              <span className="text-white">Update Ticket #{selectedTicket?.ticketCode}</span>
            </ModalHeader>
            <ModalBody className="p-4">
              <div className="mb-3">
                <Label className="fw-bold text-primary">Update Status</Label>
                <Input
                  type="select"
                  value={updateData.statusId}
                  onChange={(e) => setUpdateData({ ...updateData, statusId: parseInt(e.target.value) })}
                  className="form-select border-primary"
                >
                  {statusList.map((s) => {
                    const isAdminOrCreator = isEditUser || session.userId === selectedTicket?.createdBy;
                    const isAssignee = session.employeeId === selectedTicket?.assignedTo;
                    const curr = selectedTicket?.statusId;
                    
                    let allowed = false;
                    
                    if (s.value === curr) {
                        allowed = true; // Always allow current state
                    } else if (isAdminOrCreator) {
                        // Admin / Creator Workflow
                        if (curr === 1) allowed = [2, 3, 4, 8].includes(s.value);
                        else if (curr === 2 || curr === 5 || curr === 3 || curr === 8) allowed = [1, 2, 3, 4, 7, 8].includes(s.value);
                        else if (curr === 7) allowed = [6, 5, 2, 3, 4, 8].includes(s.value);
                        else if (curr === 6) allowed = [5].includes(s.value);
                    } else if (isAssignee && !isAdminOrCreator) {
                        // Technician Workflow
                        if (curr === 2 || curr === 5) allowed = [3, 7, 8].includes(s.value);
                        else if (curr === 3) allowed = [2, 7, 8].includes(s.value);
                        else if (curr === 8) allowed = [3, 7].includes(s.value);
                    }
                    
                    if (!allowed) return null;
                    return <option key={s.value} value={s.value}>{s.label}</option>;
                  })}
                </Input>
              </div>
              
              {/* Only Admin/Edit user can change technician */}
              {isEditUser && (
                <div className="mb-3">
                  <Label className="fw-bold text-primary">Change Assignee (Optional)</Label>
                  <Select
                    value={updateData.assignedTo}
                    onChange={(opt) => {
                      const newUpdateData = { ...updateData, assignedTo: opt };
                      if (!opt) {
                        // If employee is removed, automatically change status to Open (1)
                        newUpdateData.statusId = 1;
                      } else if (opt && updateData.statusId === 1) {
                        // If unassigned ticket gets assigned, automatically change status to Assigned (2)
                        newUpdateData.statusId = 2;
                      }
                      setUpdateData(newUpdateData);
                    }}
                    options={technicianSearchOptions}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        if (searchTimeout) clearTimeout(searchTimeout);
                        const timeout = setTimeout(() => {
                          searchTechniciansManually(inputValue);
                        }, 400);
                        setSearchTimeout(timeout);
                      }
                    }}
                    isLoading={technicianSearchLoading}
                    noOptionsMessage={({ inputValue }) => {
                      if (technicianSearchLoading) return "Searching...";
                      if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                      return "No technicians found";
                    }}
                    placeholder="Search & Select..."
                    styles={selectStyles}
                    isClearable
                  />
                </div>
              )}

              <div className="mb-3">
                <Label className="fw-bold text-primary">Update Remarks</Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={updateData.comment}
                  onChange={(e) => setUpdateData({ ...updateData, comment: e.target.value })}
                  placeholder="Detail the work done or reason for hold..."
                  className="border-primary"
                />
              </div>

              <div className="mb-3">
                 <Label className="fw-bold text-primary">Cost</Label>
                 <Input
                   type="number"
                   value={updateData.cost}
                   onChange={(e) => setUpdateData({ ...updateData, cost: e.target.value })}
                   placeholder="0.00"
                   className="border-primary"
                 />
              </div>
            </ModalBody>
            <ModalFooter className="bg-light">
              <Button color="secondary" outline onClick={() => setModal(false)}>Cancel</Button>
              <Button color="primary" onClick={handleUpdateTicket}>Save Changes</Button>
            </ModalFooter>
          </Modal>

          {/* View Ticket Details Modal */}
          <Modal isOpen={viewModal} toggle={() => setViewModal(!viewModal)} size="lg" centered>
            <ModalHeader toggle={() => setViewModal(!viewModal)} className="bg-info text-white" style={{ color: '#fff' }}>
              <span className="text-white">Ticket Detail - #{selectedTicket?.ticketCode}</span>
            </ModalHeader>
            <ModalBody className="bg-light p-0">
               <div className="p-4 bg-white border-bottom shadow-sm">
                  <Row>
                     <Col md={8}>
                        <h6 className="text-muted text-uppercase fw-bold small mb-2">Issue Description</h6>
                        <p className="fs-5 mb-0 fw-medium text-dark">{selectedTicket?.description}</p>
                     </Col>
                     <Col md={4} className="text-md-end">
                        <div className="mb-2">{selectedTicket && getStatusBadge(selectedTicket.statusId)}</div>
                        <div className="small text-muted">Priority: {selectedTicket && getPriorityBadge(selectedTicket.priority)}</div>
                     </Col>
                  </Row>
                   <hr className="my-3"/>
                   <Row className="small">
                      <Col md={4}>
                         <i className="bx bx-map-pin me-1 text-info"></i>
                         <span className="text-muted">Room/Eq:</span> <span className="fw-bold text-dark ms-1">{selectedTicket?.roomName || selectedTicket?.equipmentName || "-"}</span>
                      </Col>
                      <Col md={4}>
                         <i className="bx bx-user me-1 text-info"></i>
                         <span className="text-muted">Assigned To:</span> <span className="fw-bold text-dark ms-1">{selectedTicket?.assignedToName || "Unassigned"}</span>
                      </Col>
                      <Col md={4} className="text-md-end">
                         <i className="bx bx-calendar me-1 text-info"></i>
                         <span className="text-muted">Logged:</span> <span className="fw-bold text-dark ms-1">{selectedTicket && new Date(selectedTicket.createdDate).toLocaleString()}</span>
                      </Col>
                   </Row>
                   
                   <Row className="small mt-2 border-top pt-2">
                     <Col md={4}>
                        <i className="bx bx-time me-1 text-warning"></i>
                        <span className="text-muted">Assigned Date:</span> <span className="fw-bold text-dark ms-1">{selectedTicket?.assignedDate ? new Date(selectedTicket.assignedDate).toLocaleString() : "Not Yet Assigned"}</span>
                     </Col>
                     <Col md={4}>
                        <i className="bx bx-hourglass me-1 text-primary"></i>
                        <span className="text-muted" style={{fontSize: '0.75rem'}}>Time Passed:</span> 
                        <span className="fw-bold text-primary ms-1" style={{fontSize: '0.75rem'}}>
                          {selectedTicket && calculateDuration(selectedTicket.createdDate, selectedTicket.closedDate || selectedTicket.resolvedDate || undefined)}
                        </span>
                     </Col>
                     <Col md={4} className="text-md-end">
                        <i className="bx bx-bolt-circle me-1 text-primary"></i>
                        <span className="text-muted" style={{fontSize: '0.75rem'}}>Time Taken:</span> 
                        <span className="fw-bold text-primary ms-1" style={{fontSize: '0.75rem'}}>
                          {selectedTicket?.assignedDate ? calculateDuration(selectedTicket.assignedDate, selectedTicket.closedDate || selectedTicket.resolvedDate || undefined) : "-"}
                        </span>
                     </Col>
                   </Row>

                   <Row className="small mt-2 border-top pt-2">
                     <Col md={12} className="text-md-end">
                        <i className="bx bx-money me-1 text-success"></i>
                        <span className="text-muted" style={{fontSize: '0.75rem'}}>Total Cost:</span> <span className="fw-bold text-success ms-1" style={{fontSize: '0.75rem'}}>{selectedTicket?.cost || "0.00"}</span>
                     </Col>
                   </Row>
               </div>
               
               <div className="p-4" style={{ maxHeight: '450px', overflowY: 'auto', background: '#f8f9fa' }}>
                  <h6 className="text-muted text-uppercase fw-bold small mb-4">
                     <i className="bx bx-comment-detail me-2"></i>Communication History
                  </h6>
                  {viewLoading ? (
                    <div className="text-center py-5">
                       <i className="bx bx-loader bx-spin display-4 text-primary"></i>
                       <p className="mt-2 text-muted">Fetching conversation history...</p>
                    </div>
                  ) : (
                    <div className="chat-history">
                       {ticketLogs.length === 0 ? (
                         <div className="text-center py-5 border rounded bg-white mt-2">
                            <i className="bx bx-chat display-4 text-light mb-2"></i>
                            <div className="text-muted small italic">No comments or activity recorded yet.</div>
                         </div>
                       ) : (
                         ticketLogs.map((log, idx) => {
                            // Dynamic color assignment based on userId
                            const colorPool = ['primary', 'success', 'warning', 'danger', 'info', 'secondary'];
                            const colorScheme = colorPool[log.actionBy % colorPool.length];
                            const isMe = log.actionBy === session.userId;

                            return (
                               <div key={idx} className={`d-flex mb-4 ${log.comments ? 'flex-column' : 'justify-content-center'}`}>
                                  {log.comments ? (
                                    <>
                                       <div className={`d-flex align-items-center mb-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                                          <div className="avatar-xs mx-2">
                                             <span className={`avatar-title rounded-circle bg-soft-${colorScheme} text-${colorScheme} fw-bold`}>
                                                {log.actionByName?.substring(0, 1).toUpperCase() || "U"}
                                             </span>
                                          </div>
                                          <div>
                                             <span className={`fw-bold small text-${colorScheme}`}>{log.actionByName}</span>
                                             <span className="text-muted tiny mx-2" style={{fontSize: '0.65rem'}}>{new Date(log.actionDate).toLocaleString([], {hour: '2-digit', minute:'2-digit', month:'short', day:'numeric'})}</span>
                                          </div>
                                       </div>
                                       <div className={`p-3 rounded-lg shadow-sm border-0 ${isMe ? 'bg-white text-dark ms-auto' : 'bg-white text-dark me-auto'} border-start border-4 border-${colorScheme}`} 
                                          style={{ 
                                             maxWidth: '85%', 
                                             width: 'fit-content',
                                             borderRadius: '12px',
                                             boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                          }}>
                                          <div className="fw-medium mb-1">{log.comments}</div>
                                          {log.actionDescription && (
                                             <div className="mt-2 pt-1 border-top small italic text-muted d-flex align-items-center" style={{fontSize: '0.75rem'}}>
                                                <i className="bx bx-info-circle me-1"></i> {log.actionDescription}
                                             </div>
                                          )}
                                       </div>
                                    </>
                                  ) : (
                                    <div className="badge bg-soft-secondary text-muted border-0 px-3 py-1 rounded-pill small fw-normal">
                                       <i className="bx bx-history me-1"></i> {log.actionDescription} - {new Date(log.actionDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </div>
                                  )}
                               </div>
                            )
                         })
                       )}
                    </div>
                  )}
               </div>
            </ModalBody>
          </Modal>

          {/* Quick Add Room Modal */}
          <Modal isOpen={roomModal} toggle={() => setRoomModal(!roomModal)} centered>
            <ModalHeader toggle={() => setRoomModal(!roomModal)} className="bg-info text-white">
              <span className="text-white">Quick Add Room</span>
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={handleQuickAddRoom}>
                <FormGroup className="mb-3">
                  <Label>Room Name (English) <span className="text-danger">*</span></Label>
                  <Input 
                    type="text" 
                    value={roomFormData.Room_Name_English} 
                    onChange={(e) => setRoomFormData({...roomFormData, Room_Name_English: e.target.value})} 
                    required 
                  />
                </FormGroup>
                <FormGroup className="mb-3">
                  <Label>Room Name (Arabic)</Label>
                  <Input 
                    type="text" 
                    value={roomFormData.Room_Name_Arabic} 
                    onChange={(e) => setRoomFormData({...roomFormData, Room_Name_Arabic: e.target.value})} 
                  />
                </FormGroup>
                <div className="d-flex justify-content-end gap-2">
                  <Button type="button" color="secondary" onClick={() => setRoomModal(false)}>Cancel</Button>
                  <Button type="submit" color="info" disabled={roomLoading}>
                    {roomLoading ? <i className="bx bx-loader bx-spin me-1"></i> : null}
                    Add Room
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>

          {/* Quick Add Equipment Modal */}
          <Modal isOpen={equipModal} toggle={() => setEquipModal(!equipModal)} centered>
            <ModalHeader toggle={() => setEquipModal(!equipModal)} className="bg-info text-white">
              <span className="text-white">Quick Add Equipment</span>
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={handleQuickAddEquipment}>
                <FormGroup className="mb-3">
                  <Label>Equipment Name (English) <span className="text-danger">*</span></Label>
                  <Input 
                    type="text" 
                    value={equipFormData.Eq_Name_English} 
                    onChange={(e) => setEquipFormData({...equipFormData, Eq_Name_English: e.target.value})} 
                    required 
                  />
                </FormGroup>
                <FormGroup className="mb-3">
                  <Label>Equipment Name (Arabic)</Label>
                  <Input 
                    type="text" 
                    value={equipFormData.Eq_Name_Arabic} 
                    onChange={(e) => setEquipFormData({...equipFormData, Eq_Name_Arabic: e.target.value})} 
                  />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Purchase Date <span className="text-danger">*</span></Label>
                      <Input 
                        type="date" 
                        value={equipFormData.PurchaseDate} 
                        onChange={(e) => setEquipFormData({...equipFormData, PurchaseDate: e.target.value})} 
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Value Date <span className="text-danger">*</span></Label>
                      <Input 
                        type="date" 
                        value={equipFormData.ValueDate} 
                        onChange={(e) => setEquipFormData({...equipFormData, ValueDate: e.target.value})} 
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end gap-2">
                  <Button type="button" color="secondary" onClick={() => setEquipModal(false)}>Cancel</Button>
                  <Button type="submit" color="info" disabled={equipLoading}>
                    {equipLoading ? <i className="bx bx-loader bx-spin me-1"></i> : null}
                    Add Equipment
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>

        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(GmsMaintenanceTicketsPage);
